// src/components/NotificationPanel.tsx
import React, { useState, useEffect } from "react";
import { FiBell, FiCheck, FiX, FiFolder, FiBriefcase, FiClock } from "react-icons/fi";
import { toast } from "react-hot-toast";
import { 
  getUserNotifications, 
  getUnreadNotificationCount,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification
} from "../services/notifications";
import { useSupabase } from "../hooks/useSupabase";
import type { TaskNotification } from "../services/notifications";
import "./NotificationPanel.css";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export default function NotificationPanel({ isOpen, onClose }: Props) {
  const { user } = useSupabase();
  const [notifications, setNotifications] = useState<TaskNotification[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && user) {
      loadNotifications();
    }
  }, [isOpen, user]);

  const loadNotifications = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const { data, error } = await getUserNotifications(user.id);
      if (error) {
        console.warn("Notifications not available:", error.message);
        setNotifications([]);
      } else if (data) {
        setNotifications(data);
      }
    } catch (error) {
      console.warn("Error loading notifications:", error);
      setNotifications([]);
    }
    setLoading(false);
  };

  const handleMarkAsRead = async (notificationId: string) => {
    const { error } = await markNotificationAsRead(notificationId);
    if (error) {
      toast.error("Error marcando como leída");
    } else {
      setNotifications(prev => 
        prev.filter(n => n.id !== notificationId)
      );
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!user) return;
    
    const { error } = await markAllNotificationsAsRead(user.id);
    if (error) {
      toast.error("Error marcando todas como leídas");
    } else {
      setNotifications([]);
      toast.success("Todas las notificaciones marcadas como leídas");
    }
  };

  const handleDeleteNotification = async (notificationId: string) => {
    const { error } = await deleteNotification(notificationId);
    if (error) {
      toast.error("Error eliminando notificación");
    } else {
      setNotifications(prev => 
        prev.filter(n => n.id !== notificationId)
      );
    }
  };

  const formatNotificationTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return "Hace un momento";
    if (diffInMinutes < 60) return `Hace ${diffInMinutes} min`;
    if (diffInMinutes < 1440) return `Hace ${Math.floor(diffInMinutes / 60)} h`;
    return date.toLocaleDateString();
  };

  if (!isOpen) return null;

  return (
    <div className="notification-panel-overlay" onClick={onClose}>
      <div className="notification-panel" onClick={e => e.stopPropagation()}>
        <div className="notification-panel-header">
          <div className="notification-header-title">
            <FiBell size={18} />
            <h3>Notificaciones</h3>
            {notifications.length > 0 && (
              <span className="notification-count">{notifications.length}</span>
            )}
          </div>
          
          <div className="notification-header-actions">
            {notifications.length > 0 && (
              <button 
                className="mark-all-read-btn"
                onClick={handleMarkAllAsRead}
                title="Marcar todas como leídas"
              >
                <FiCheck size={16} />
              </button>
            )}
            <button 
              className="close-panel-btn"
              onClick={onClose}
              title="Cerrar"
            >
              <FiX size={16} />
            </button>
          </div>
        </div>

        <div className="notification-panel-content">
          {loading ? (
            <div className="notification-loading">
              <div className="loading-spinner"></div>
              Cargando notificaciones...
            </div>
          ) : notifications.length === 0 ? (
            <div className="no-notifications">
              <FiBell size={48} />
              <h4>No tienes notificaciones</h4>
              <p>Te notificaremos cuando te asignen nuevas tareas</p>
            </div>
          ) : (
            <div className="notifications-list">
              {notifications.map((notification) => (
                <div key={notification.id} className="notification-item">
                  <div className="notification-content">
                    <div className="notification-icon">
                      {notification.notification_type === 'assigned' && <FiBell size={16} />}
                      {notification.notification_type === 'updated' && <FiClock size={16} />}
                      {notification.notification_type === 'completed' && <FiCheck size={16} />}
                    </div>
                    
                    <div className="notification-details">
                      <div className="notification-message">
                        {notification.message}
                      </div>
                      
                      {notification.task && (
                        <div className="notification-task-info">
                          <span className="task-title">
                            {notification.task.title}
                          </span>
                          {notification.task.project && (
                            <div className="task-location">
                              <FiFolder size={12} />
                              <span>{notification.task.project.name}</span>
                              {notification.task.project.company && (
                                <>
                                  <span className="separator">•</span>
                                  <FiBriefcase size={12} />
                                  <span>{notification.task.project.company.name}</span>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="notification-time">
                        {formatNotificationTime(notification.created_at)}
                      </div>
                    </div>
                  </div>
                  
                  <div className="notification-actions">
                    <button
                      className="notification-action-btn mark-read"
                      onClick={() => handleMarkAsRead(notification.id)}
                      title="Marcar como leída"
                    >
                      <FiCheck size={14} />
                    </button>
                    <button
                      className="notification-action-btn delete"
                      onClick={() => handleDeleteNotification(notification.id)}
                      title="Eliminar"
                    >
                      <FiX size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Hook para usar las notificaciones en otros componentes
export function useNotifications() {
  const { user } = useSupabase();
  const [unreadCount, setUnreadCount] = useState(0);

  const refreshUnreadCount = async () => {
    if (!user) return;
    
    try {
      const { count, error } = await getUnreadNotificationCount(user.id);
      if (!error) {
        setUnreadCount(count);
      } else {
        // Si la tabla no existe o hay otro error, simplemente no mostrar notificaciones
        console.warn('Notifications table not available:', error.message);
        setUnreadCount(0);
      }
    } catch (error) {
      console.warn('Error fetching notifications:', error);
      setUnreadCount(0);
    }
  };

  useEffect(() => {
    refreshUnreadCount();
    
    // Refrescar cada 30 segundos
    const interval = setInterval(refreshUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [user]);

  return {
    unreadCount,
    refreshUnreadCount
  };
}